/**
 * Media Upload Constants
 *
 * Centralized constants for media upload functionality across the application.
 * Used by design system MediaUpload component and feature-specific media upload components.
 */

// Maximum file sizes in MB
export const MEDIA_UPLOAD_MAX_FILE_SIZE = {
  IMAGE: 5,
  VIDEO: 16,
  DOCUMENT: 50,
} as const;

export const MEDIA_UPLOAD_LIMIT_TEXT = {
  IMAGE: `Images: max ${MEDIA_UPLOAD_MAX_FILE_SIZE.IMAGE}MB`,
  VIDEO: `Videos: max ${MEDIA_UPLOAD_MAX_FILE_SIZE.VIDEO}MB`,
  DOCUMENT: `Files: max ${MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT}MB`,
  ALL: `Images: max ${MEDIA_UPLOAD_MAX_FILE_SIZE.IMAGE}MB | Videos: max ${MEDIA_UPLOAD_MAX_FILE_SIZE.VIDEO}MB | Files: max ${MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT}MB`,
} as const;

// Accepted file formats by media type
export const MEDIA_UPLOAD_ACCEPTED_FORMATS = {
  IMAGE: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'],
  VIDEO: ['mp4', '3gp', 'mov', 'avi', 'webm'],
  DOCUMENT: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'],
} as const;

// Combined formats for 'any' media type
export const MEDIA_UPLOAD_ALL_FORMATS = [
  ...MEDIA_UPLOAD_ACCEPTED_FORMATS.IMAGE,
  ...MEDIA_UPLOAD_ACCEPTED_FORMATS.VIDEO,
  ...MEDIA_UPLOAD_ACCEPTED_FORMATS.DOCUMENT,
] as const;

// File configuration for each media type
export const MEDIA_UPLOAD_FILE_CONFIG = {
  image: {
    maxSize: MEDIA_UPLOAD_MAX_FILE_SIZE.IMAGE,
    acceptedFormats: [...MEDIA_UPLOAD_ACCEPTED_FORMATS.IMAGE],
  },
  video: {
    maxSize: MEDIA_UPLOAD_MAX_FILE_SIZE.VIDEO,
    acceptedFormats: [...MEDIA_UPLOAD_ACCEPTED_FORMATS.VIDEO],
  },
  document: {
    maxSize: MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT,
    acceptedFormats: [...MEDIA_UPLOAD_ACCEPTED_FORMATS.DOCUMENT],
  },
  any: {
    maxSize: MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT,
    acceptedFormats: [...MEDIA_UPLOAD_ALL_FORMATS],
  },
} as const;

// Media type labels for display
export const MEDIA_TYPE_LABELS = {
  image: 'Image file',
  video: 'Video file',
  document: 'Document',
  any: 'File',
} as const;

// Image extensions for file type detection (includes GIF for animations)
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'] as const;

// Video extensions for file type detection
export const VIDEO_EXTENSIONS = ['mp4', '3gp', 'mov', 'avi', 'webm'] as const;

// Document extensions for file type detection
export const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'] as const;

// Types
export type MediaType = 'image' | 'video' | 'document' | 'any';
export type FileConfigType = typeof MEDIA_UPLOAD_FILE_CONFIG;
